import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from 'smart-react';
import {
  ArrowUpDown,
  Archive,
  Bug,
  ChevronDown,
  ChevronUp,
  PencilLine,
  Package2,
} from 'lucide-react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import Loader from '../../Core/Components/Loader/Loader';
import { useAuth } from '../../Core/Context/AuthContext';
import { Button } from '@/Modules/Core/Components/shadcn/button';
import { SplitButton } from '@/Modules/Core/Components/shadcn/split-button';
import TagInput from '@/Modules/Core/Components/Tags/TagInput';
import TagBadgeList from '@/Modules/Core/Components/Tags/TagBadgeList';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/Modules/Core/Components/shadcn/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Modules/Core/Components/shadcn/select';
import { AppPageSection } from '@/Modules/Core/Components/Layout/AppPage';
import { EmptyState } from '@/Modules/Core/Components/Layout/EmptyState';
import { PaginationControls } from '@/Modules/Core/Components/Layout/PaginationControls';
import RolloutCatalogHeader from '../Components/RolloutCatalog/RolloutCatalogHeader';
import RolloutCatalogFilters from '../Components/RolloutCatalog/RolloutCatalogFilters';
import RolloutCatalogSkeleton from '../Components/RolloutCatalog/RolloutCatalogSkeleton';
import RolloutCatalogTable, {
  Checkbox,
} from '../Components/RolloutCatalog/RolloutCatalogTable';
import RolloutBugsDialog from '../Components/RolloutCatalog/RolloutBugsDialog';
import useRolloutCatalog from '../hooks/useRolloutCatalog';
import {
  deleteRolloutData,
  downloadRolloutArchiveByEnvironment,
  downloadRolloutArchiveDefault,
  downloadRolloutArchives,
  downloadRolloutBug,
} from '../Services/SearchFilesService';
import {
  searchRolloutTags,
  updateRolloutTags,
} from '@/Modules/Core/Services/RolloutTagService';
import {
  flattenRolloutTree,
  getRolloutMajorVersion,
  getRolloutVersionId,
  mapRolloutTree,
} from '../Components/RolloutCatalog/rolloutCatalogUtils';
import {
  getTagSearchText,
  mergeTagOptions,
  normalizeTagList,
} from '@/Modules/Core/Components/Tags/tagUtils';

const SortableHeader = ({ column, label }) => {
  const sorted = column.getIsSorted();

  return (
    <button
      type="button"
      className={
        sorted
          ? 'inline-flex w-full appearance-none items-center gap-2 rounded-lg border-0 bg-transparent px-2 py-1 text-left text-foreground shadow-none outline-none transition-colors'
          : 'inline-flex w-full appearance-none items-center gap-2 rounded-lg border-0 bg-transparent px-2 py-1 text-left text-muted-foreground shadow-none outline-none transition-colors hover:bg-primary/4 hover:text-foreground'
      }
      onClick={column.getToggleSortingHandler()}
    >
      <span>{label}</span>
      {sorted === 'asc' ? (
        <ChevronUp className="h-3.5 w-3.5" />
      ) : sorted === 'desc' ? (
        <ChevronDown className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown className="h-3.5 w-3.5 opacity-70" />
      )}
    </button>
  );
};

const normalizeConfiguredPlatform = (value) => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();

  if (['tar', 'linux', 'l'].includes(normalized)) {
    return {
      label: 'Download TAR',
      shortLabel: 'TAR',
      platformType: 'l',
    };
  }

  return {
    label: 'Download ZIP',
    shortLabel: 'ZIP',
    platformType: 'w',
  };
};

const DOWNLOAD_OPTIONS = [
  {
    id: 'zip',
    label: 'Download ZIP',
    shortLabel: 'ZIP',
    platformType: 'w',
    icon: Package2,
  },
  {
    id: 'tar',
    label: 'Download TAR',
    shortLabel: 'TAR',
    platformType: 'l',
    icon: Archive,
  },
];

const rolloutEnvironmentConfigKey = 'MOCA_ENVIRONEMTNS';

const parseRolloutEnvironmentOptions = (rawValue) => {
  if (!rawValue) {
    return [];
  }

  const parsedValue =
    typeof rawValue === 'string'
      ? (() => {
          try {
            return JSON.parse(rawValue);
          } catch (_error) {
            return [];
          }
        })()
      : rawValue;

  if (!Array.isArray(parsedValue)) {
    return [];
  }

  const dedupedOptions = new Map();

  parsedValue.forEach((item) => {
    const environmentName = String(
      item?.environment ?? item?.name ?? item?.label ?? ''
    ).trim();
    const isDefault = Boolean(
      item?.isDefault ?? item?.default ?? item?.is_default
    );

    if (!environmentName) {
      return;
    }

    const normalizedKey = environmentName.toLowerCase();

    if (!dedupedOptions.has(normalizedKey)) {
      dedupedOptions.set(normalizedKey, {
        value: environmentName,
        label: environmentName,
        majorVersion: String(
          item?.majorVersion ?? item?.major ?? item?.major_version ?? ''
        ).trim(),
        minorVersion: String(
          item?.minorVersion ?? item?.minor ?? item?.minor_version ?? ''
        ).trim(),
        isDefault,
      });
      return;
    }

    if (isDefault) {
      dedupedOptions.set(normalizedKey, {
        ...dedupedOptions.get(normalizedKey),
        isDefault: true,
      });
    }
  });

  return Array.from(dedupedOptions.values());
};

const formatConfiguredVersionValue = (versionOption) => {
  const majorVersion = String(versionOption?.majorVersion ?? '').trim();
  const minorVersion = String(versionOption?.minorVersion ?? '').trim();

  if (majorVersion && minorVersion) {
    return `${majorVersion}.${minorVersion}`;
  }

  return majorVersion || minorVersion || 'Version not configured';
};

const getDefaultConfiguredVersionOption = (options = []) =>
  options.find((option) => Boolean(option?.isDefault)) || options[0] || null;

const legacyRolloutDownloadFallbackMessages = [
  '404',
  'not found',
  'cannot get',
  'method not allowed',
  'unsupported',
];

const shouldFallbackToLegacyRolloutDownload = (response) => {
  if (response?.isSuccess) {
    return false;
  }

  const normalizedMessage = String(response?.message ?? '').trim().toLowerCase();

  return legacyRolloutDownloadFallbackMessages.some((pattern) =>
    normalizedMessage.includes(pattern)
  );
};

const triggerRolloutDownload = (downloadUrl) => {
  const resolvedUrl = String(downloadUrl ?? '').trim();

  if (!resolvedUrl || typeof document === 'undefined') {
    return false;
  }

  const downloadFrame = document.createElement('iframe');
  downloadFrame.style.display = 'none';
  downloadFrame.src = resolvedUrl;
  document.body.appendChild(downloadFrame);

  window.setTimeout(() => {
    downloadFrame.remove();
  }, 60_000);

  return true;
};

const RolloutDescriptionCell = ({ description }) => (
  <div className="w-full max-w-full overflow-hidden">
    <p
      className="block truncate text-[12px] leading-5 text-muted-foreground"
      title={description}
    >
      {description}
    </p>
  </div>
);

const CONTEXT_BADGE_BASE_CLASS =
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium';
const ENVIRONMENT_BADGE_CLASS = `${CONTEXT_BADGE_BASE_CLASS} border border-sky-500/20 bg-sky-500/10 text-sky-700 dark:text-sky-300`;
const ROLLOUT_TYPE_BADGE_CLASS = `${CONTEXT_BADGE_BASE_CLASS} border border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300`;
const GROUP_ROW_INDENT_PX = 28;

const getRolloutRowId = (rollout) =>
  String(rollout?.rolloutId ?? rollout?.rolloutName ?? rollout?.key);

const hasRolloutChildren = (rollout) =>
  Array.isArray(rollout?.children) && rollout.children.length > 0;

const matchesRolloutSearch = (rollout, filterValue) => {
  const query = String(filterValue ?? '')
    .trim()
    .toLowerCase();

  if (!query) {
    return true;
  }

  return [
    rollout?.rolloutName,
    rollout?.description,
    rollout?.environment,
    rollout?.rolloutType,
    rollout?.rolloutTypeLabel,
    rollout?.createdAtLabel,
    String(rollout?.totalBugs ?? ''),
    rollout?.tagSearchText,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(query));
};

const RolloutList = () => {
  const navigate = useNavigate();
  const { projectId, versionId } = useParams();
  const location = useLocation();
  const { state } = location;
  const { configData, hasPermission } = useAuth();
  const { downloadRolloutStatus, rolloutPlatform, downloadRolloutName } =
    state || {};
  const sanitizedLocationState = React.useMemo(() => {
    if (!state) {
      return undefined;
    }

    const {
      downloadRolloutStatus: _downloadRolloutStatus,
      rolloutPlatform: _rolloutPlatform,
      downloadRolloutName: _downloadRolloutName,
      ...rest
    } = state;

    return Object.keys(rest).length > 0 ? rest : undefined;
  }, [state]);
  const rolloutQueryParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const projectName = String(
    state?.projectName ?? rolloutQueryParams.get('projectName') ?? ''
  ).trim();
  const versionName = String(
    state?.versionName ?? rolloutQueryParams.get('versionName') ?? ''
  ).trim();
  const rolloutContextState = React.useMemo(
    () => ({
      ...(projectName ? { projectName } : {}),
      ...(versionName ? { versionName } : {}),
    }),
    [projectName, versionName]
  );

  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [filterTagSuggestions, setFilterTagSuggestions] = React.useState([]);
  const [filterTagSearchQuery, setFilterTagSearchQuery] = React.useState('');
  const [filterTagSuggestionsLoading, setFilterTagSuggestionsLoading] =
    React.useState(false);
  const [editorTagSuggestions, setEditorTagSuggestions] = React.useState([]);
  const [editorTagSearchQuery, setEditorTagSearchQuery] = React.useState('');
  const [editorTagSuggestionsLoading, setEditorTagSuggestionsLoading] =
    React.useState(false);
  const [rolloutTagOverrides, setRolloutTagOverrides] = React.useState({});
  const [actionLoading, setActionLoading] = React.useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = React.useState(
    Boolean(downloadRolloutStatus)
  );
  const [downloadDialogRollout, setDownloadDialogRollout] = React.useState({
    platformType: rolloutPlatform || '',
    rolloutName: downloadRolloutName || '',
  });
  const [versionDownloadDialog, setVersionDownloadDialog] = React.useState({
    open: false,
    rolloutName: '',
    rolloutVersionId: '',
  });
  const [selectedConfiguredVersion, setSelectedConfiguredVersion] =
    React.useState('');
  const [deleteRolloutNames, setDeleteRolloutNames] = React.useState([]);
  const [bugsRolloutName, setBugsRolloutName] = React.useState(null);
  const [editingRollout, setEditingRollout] = React.useState(null);
  const [editingTags, setEditingTags] = React.useState([]);
  const [savingTags, setSavingTags] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState({
    type: 'error',
    message: '',
  });
  const [sorting, setSorting] = React.useState([
    { id: 'createdAt', desc: true },
  ]);
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] = React.useState({});
  const [columnSizing, setColumnSizing] = React.useState({});
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [expanded, setExpanded] = React.useState({});
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('');
  const canCreateRollout = hasPermission('rollouts.create');
  const canDeleteRollouts = hasPermission('rollouts.delete');
  const canDownloadRollouts = hasPermission('rollouts.download');
  const canUpdateTags = hasPermission('rollouts.tags.update');
  const canViewIssues = hasPermission('issues.view');

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 650);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  React.useEffect(() => {
    if (!downloadRolloutStatus) {
      return;
    }

    setDownloadDialogRollout({
      platformType: rolloutPlatform || '',
      rolloutName: downloadRolloutName || '',
    });
    setDownloadDialogRollout({
      platformType: rolloutPlatform || '',
      rolloutName: downloadRolloutName || '',
    });
    setDownloadDialogOpen(true);
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: sanitizedLocationState,
      }
    );
  }, [
    downloadRolloutStatus,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    downloadRolloutName,
    rolloutPlatform,
    sanitizedLocationState,
  ]);

  const {
    errorMessage,
    loading,
    refreshing,
    rollouts,
    refreshRollouts,
    reportedTotal,
  } = useRolloutCatalog({
    projectId,
    versionId,
    searchTerm: debouncedSearchTerm,
    selectedTags,
    sorting,
  });

  const preferredDownload = React.useMemo(
    () => normalizeConfiguredPlatform(configData?.DOWNLOAD_PLATFORM),
    [configData?.DOWNLOAD_PLATFORM]
  );
  const preferredDownloadOption = React.useMemo(
    () =>
      DOWNLOAD_OPTIONS.find(
        (option) => option.platformType === preferredDownload.platformType
      ) ?? DOWNLOAD_OPTIONS[0],
    [preferredDownload.platformType]
  );
  const alternateDownloadOptions = React.useMemo(
    () =>
      DOWNLOAD_OPTIONS.filter(
        (option) => option.platformType !== preferredDownloadOption.platformType
      ),
    [preferredDownloadOption.platformType]
  );
  const configuredVersionOptions = React.useMemo(
    () => parseRolloutEnvironmentOptions(configData?.[rolloutEnvironmentConfigKey]),
    [configData]
  );
  const selectedConfiguredVersionOption = React.useMemo(
    () =>
      configuredVersionOptions.find(
        (option) =>
          String(option?.value ?? '').trim() ===
          String(selectedConfiguredVersion ?? '').trim()
      ) || null,
    [configuredVersionOptions, selectedConfiguredVersion]
  );
  const downloadDialogPlatformOption = React.useMemo(
    () =>
      DOWNLOAD_OPTIONS.find(
        (option) =>
          option.platformType ===
          String(downloadDialogRollout.platformType ?? '').trim()
      ) || null,
    [downloadDialogRollout.platformType]
  );
  const hydratedRollouts = React.useMemo(
    () =>
      mapRolloutTree(rollouts, (rollout) => {
        const overriddenTags = rolloutTagOverrides[rollout.rolloutName];

        if (!overriddenTags) {
          return rollout;
        }

        return {
          ...rollout,
          tags: overriddenTags,
          tagSearchText: getTagSearchText(overriddenTags),
        };
      }),
    [rolloutTagOverrides, rollouts]
  );
  const flattenedHydratedRollouts = React.useMemo(
    () => flattenRolloutTree(hydratedRollouts),
    [hydratedRollouts]
  );
  const dynamicTagOptions = React.useMemo(
    () =>
      mergeTagOptions(
        filterTagSuggestions,
        flattenedHydratedRollouts.flatMap((rollout) => rollout.tags),
        selectedTags
      ),
    [filterTagSuggestions, flattenedHydratedRollouts, selectedTags]
  );
  const editorTagOptions = React.useMemo(
    () =>
      mergeTagOptions(
        editorTagSuggestions,
        flattenedHydratedRollouts.flatMap((rollout) => rollout.tags),
        selectedTags,
        editingTags
      ),
    [editingTags, editorTagSuggestions, flattenedHydratedRollouts, selectedTags]
  );
  const hasInteractiveFilters =
    searchTerm.trim() !== '' || selectedTags.length > 0;
  const autoExpandedGroups = React.useMemo(() => {
    if (!hasInteractiveFilters) {
      return {};
    }

    return flattenedHydratedRollouts.reduce((nextExpanded, rollout) => {
      if (!hasRolloutChildren(rollout)) {
        return nextExpanded;
      }

      nextExpanded[getRolloutRowId(rollout)] = true;
      return nextExpanded;
    }, {});
  }, [flattenedHydratedRollouts, hasInteractiveFilters]);
  // Filters auto-expand groups by default; explicit user toggles win.
  const expandedState = React.useMemo(
    () =>
      hasInteractiveFilters
        ? {
            ...autoExpandedGroups,
            ...expanded,
          }
        : expanded,
    [autoExpandedGroups, expanded, hasInteractiveFilters]
  );
  const handleExpandedChange = React.useCallback(
    (updater) => {
      const next =
        typeof updater === 'function' ? updater(expandedState) : updater;
      const resolved = { ...next };

      // TanStack deletes collapsed keys; keep them as explicit false so a
      // collapsed group doesn't fall back to the filter's auto-expand.
      Object.keys(expandedState).forEach((id) => {
        if (expandedState[id] && !resolved[id]) {
          resolved[id] = false;
        }
      });
      setExpanded(resolved);
    },
    [expandedState]
  );
  const showAlert = React.useCallback((message, type = 'error') => {
    setAlertMessage({
      type,
      message,
    });
  }, []);

  const clearAlert = React.useCallback(() => {
    setAlertMessage({
      type: 'error',
      message: '',
    });
  }, []);

  const clearSelection = React.useCallback(() => {
    setRowSelection({});
  }, []);

  React.useEffect(() => {
    let isActive = true;

    const loadFilterTagSuggestions = async () => {
      setFilterTagSuggestionsLoading(true);

      try {
        const response = await searchRolloutTags({
          search: filterTagSearchQuery,
        });

        if (!isActive) {
          return;
        }

        if (response?.tags?.length > 0) {
          setFilterTagSuggestions((previousOptions) =>
            mergeTagOptions(previousOptions, response.tags)
          );
        }
      } finally {
        if (isActive) {
          setFilterTagSuggestionsLoading(false);
        }
      }
    };

    loadFilterTagSuggestions();

    return () => {
      isActive = false;
    };
  }, [filterTagSearchQuery]);

  React.useEffect(() => {
    let isActive = true;

    const loadTagSuggestions = async () => {
      setEditorTagSuggestionsLoading(true);

      try {
        const response = await searchRolloutTags({
          search: editorTagSearchQuery,
        });

        if (!isActive) {
          return;
        }

        if (response?.tags?.length > 0) {
          setEditorTagSuggestions((previousOptions) =>
            mergeTagOptions(previousOptions, response.tags)
          );
        }
      } finally {
        if (isActive) {
          setEditorTagSuggestionsLoading(false);
        }
      }
    };

    loadTagSuggestions();

    return () => {
      isActive = false;
    };
  }, [editorTagSearchQuery]);

  React.useEffect(() => {
    if (!downloadDialogOpen && !versionDownloadDialog.open) {
      return;
    }

    setSelectedConfiguredVersion((currentValue) => {
      const matchedOption =
        configuredVersionOptions.find(
          (option) =>
            String(option?.value ?? '').trim() ===
            String(currentValue ?? '').trim()
        ) || null;

      if (matchedOption) {
        return matchedOption.value;
      }

      return getDefaultConfiguredVersionOption(configuredVersionOptions)?.value || '';
    });
  }, [configuredVersionOptions, downloadDialogOpen, versionDownloadDialog.open]);

  const handleCloseDownloadDialog = React.useCallback(() => {
    setSelectedConfiguredVersion('');
    setDownloadDialogOpen(false);
  }, []);

  const handleCloseVersionDownloadDialog = React.useCallback(() => {
    setSelectedConfiguredVersion('');
    setVersionDownloadDialog({
      open: false,
      rolloutName: '',
      rolloutVersionId: '',
    });
  }, []);

  const handleDownloadArchive = React.useCallback(
    async (platformType, rolloutName, options = {}) => {
      if (!canDownloadRollouts) {
        return;
      }

      const {
        onSuccess,
        versionId: rolloutVersionId = '',
        environment = '',
        majorVersion = '',
        minorVersion = '',
      } = options;

      setActionLoading(true);
      clearAlert();

      try {
        const preferredResponse = environment
          ? await downloadRolloutArchiveByEnvironment(
              platformType,
              rolloutName,
              environment
            )
          : await downloadRolloutArchiveDefault(platformType, rolloutName);

        const response = shouldFallbackToLegacyRolloutDownload(
          preferredResponse
        )
          ? await downloadRolloutArchives(platformType, rolloutName, {
              versionId: rolloutVersionId,
              environment,
              majorVersion,
              minorVersion,
            })
          : preferredResponse;

        if (!response?.isSuccess) {
          showAlert(response?.message || 'Unable to download rollout archive.');
          return;
        }

        const downloadStarted = triggerRolloutDownload(
          response?.payload?.downloadUrl
        );

        if (!downloadStarted) {
          showAlert(
            response?.message || 'Download URL was not returned by the server.'
          );
          return;
        }

        if (typeof onSuccess === 'function') {
          onSuccess();
        }
      } catch (error) {
        showAlert(error?.message || 'Unable to download rollout archive.');
      } finally {
        setActionLoading(false);
      }
    },
    [canDownloadRollouts, clearAlert, showAlert]
  );

  const handleOpenVersionDownloadDialog = React.useCallback(
    (rollout) => {
      const rolloutName = String(rollout?.rolloutName ?? '').trim();
      const rolloutVersionId =
        getRolloutVersionId(rollout?.version) || String(versionId ?? '').trim();
      const defaultConfiguredVersion = getDefaultConfiguredVersionOption(
        configuredVersionOptions
      );

      if (!rolloutName) {
        return;
      }

      clearAlert();
      setSelectedConfiguredVersion(defaultConfiguredVersion?.value || '');
      setVersionDownloadDialog({
        open: true,
        rolloutName,
        rolloutVersionId,
      });
    },
    [clearAlert, configuredVersionOptions, versionId]
  );

  const handleDownloadConfiguredVersion = React.useCallback(
    async (platformType) => {
      const rolloutName = String(versionDownloadDialog.rolloutName ?? '').trim();
      const rolloutVersionId = String(
        versionDownloadDialog.rolloutVersionId ?? ''
      ).trim();
      const versionOption = selectedConfiguredVersionOption;
      const environment = String(versionOption?.value ?? '').trim();
      const majorVersion = String(versionOption?.majorVersion ?? '').trim();
      const minorVersion = String(versionOption?.minorVersion ?? '').trim();

      if (!rolloutName) {
        return;
      }

      if (!versionOption) {
        showAlert('Select a configured version to continue.');
        return;
      }

      if (!environment) {
        showAlert(
          'This configured version is missing its environment value. Please update it in Settings first.'
        );
        return;
      }

      await handleDownloadArchive(platformType, rolloutName, {
        onSuccess: handleCloseVersionDownloadDialog,
        versionId: rolloutVersionId,
        environment,
        majorVersion,
        minorVersion,
      });
    },
    [
      handleCloseVersionDownloadDialog,
      handleDownloadArchive,
      selectedConfiguredVersionOption,
      showAlert,
      versionDownloadDialog,
    ]
  );

  const handleDownloadCreatedRollout = React.useCallback(async () => {
    const rolloutName = String(downloadDialogRollout.rolloutName ?? '').trim();
    const platformType = String(downloadDialogRollout.platformType ?? '').trim();
    const versionOption = selectedConfiguredVersionOption;
    const environment = String(versionOption?.value ?? '').trim();
    const majorVersion = String(versionOption?.majorVersion ?? '').trim();
    const minorVersion = String(versionOption?.minorVersion ?? '').trim();

    if (!rolloutName || !platformType) {
      return;
    }

    if (configuredVersionOptions.length > 0 && !versionOption) {
      showAlert('Select a configured version to continue.');
      return;
    }

    if (versionOption && !environment) {
      showAlert(
        'This configured version is missing its environment value. Please update it in Settings first.'
      );
      return;
    }

    await handleDownloadArchive(platformType, rolloutName, {
      onSuccess: handleCloseDownloadDialog,
      versionId: String(versionId ?? '').trim(),
      environment,
      majorVersion,
      minorVersion,
    });
  }, [
    configuredVersionOptions.length,
    downloadDialogRollout,
    handleCloseDownloadDialog,
    handleDownloadArchive,
    selectedConfiguredVersionOption,
    showAlert,
    versionId,
  ]);

  const handleDownloadBug = React.useCallback(
    async (bugId, platformType) => {
      if (!canDownloadRollouts) {
        return;
      }

      if (!bugsRolloutName || !bugId) {
        return;
      }

      setActionLoading(true);
      clearAlert();

      try {
        const response = await downloadRolloutBug({
          rolloutName: bugsRolloutName,
          bugs: [bugId],
          platform: platformType,
        });

        if (!response?.isSuccess) {
          showAlert(response?.message || 'Unable to download bug rollout.');
          return;
        }

        if (response?.payload?.downloadUrl) {
          const downloadStarted = triggerRolloutDownload(
            response.payload.downloadUrl
          );

          if (!downloadStarted) {
            showAlert('Download URL was not returned by the server.');
          }
        } else {
          showAlert('Download URL was not returned by the server.');
        }
      } catch (error) {
        showAlert(error?.message || 'Unable to download bug rollout.');
      } finally {
        setActionLoading(false);
      }
    },
    [bugsRolloutName, canDownloadRollouts, clearAlert, showAlert]
  );

  const handleDownloadAllBugs = React.useCallback(
    async (bugIds, platformType) => {
      if (!canDownloadRollouts) {
        return;
      }

      if (!bugsRolloutName || !Array.isArray(bugIds) || bugIds.length === 0) {
        return;
      }

      setActionLoading(true);
      clearAlert();

      try {
        const response = await downloadRolloutBug({
          rolloutName: bugsRolloutName,
          bugs: bugIds,
          platform: platformType,
        });

        if (!response?.isSuccess) {
          showAlert(response?.message || 'Unable to download bug rollout.');
          return;
        }

        if (response?.payload?.downloadUrl) {
          const downloadStarted = triggerRolloutDownload(
            response.payload.downloadUrl
          );

          if (!downloadStarted) {
            showAlert('Download URL was not returned by the server.');
          }
        } else {
          showAlert('Download URL was not returned by the server.');
        }
      } catch (error) {
        showAlert(error?.message || 'Unable to download bug rollout.');
      } finally {
        setActionLoading(false);
      }
    },
    [bugsRolloutName, canDownloadRollouts, clearAlert, showAlert]
  );

  const handleRefresh = React.useCallback(async () => {
    clearAlert();
    clearSelection();
    await refreshRollouts();
  }, [clearAlert, clearSelection, refreshRollouts]);

  const handleCreateRollout = React.useCallback(() => {
    if (!canCreateRollout) {
      return;
    }

    navigate(`/createrollout/${projectId}/${versionId}/new/0`, {
      state: rolloutContextState,
    });
  }, [canCreateRollout, navigate, projectId, rolloutContextState, versionId]);

  const handleCreateAccumulative = React.useCallback(() => {
    if (!canCreateRollout) {
      return;
    }

    navigate(`/createrollout/${projectId}/${versionId}/accum/0`, {
      state: rolloutContextState,
    });
  }, [canCreateRollout, navigate, projectId, rolloutContextState, versionId]);

  const handleOpenTagEditor = React.useCallback(
    (rollout) => {
      if (!canUpdateTags) {
        return;
      }

      setEditingRollout(rollout);
      setEditingTags(normalizeTagList(rollout?.tags));
      setEditorTagSearchQuery('');
    },
    [canUpdateTags]
  );

  const handleCloseTagEditor = React.useCallback(() => {
    setEditingRollout(null);
    setEditingTags([]);
    setEditorTagSearchQuery('');
  }, []);

  const handleSaveTags = React.useCallback(async () => {
    if (!canUpdateTags) {
      return;
    }

    if (!editingRollout?.rolloutName) {
      return;
    }

    setSavingTags(true);
    clearAlert();

    try {
      const response = await updateRolloutTags({
        rolloutName: editingRollout.rolloutName,
        tags: editingTags,
      });

      if (!response?.isSuccess) {
        showAlert(response?.message || 'Unable to update rollout tags.');
        return;
      }

      const normalizedTags = normalizeTagList(editingTags);

      setRolloutTagOverrides((current) => ({
        ...current,
        [editingRollout.rolloutName]: normalizedTags,
      }));
      setEditorTagSuggestions((current) =>
        mergeTagOptions(current, normalizedTags)
      );
      handleCloseTagEditor();
      await refreshRollouts();
      showAlert(`Tags updated for ${editingRollout.rolloutName}.`, 'success');
    } catch (error) {
      showAlert(error?.message || 'Unable to update rollout tags.');
    } finally {
      setSavingTags(false);
    }
  }, [
    canUpdateTags,
    clearAlert,
    editingRollout,
    editingTags,
    handleCloseTagEditor,
    refreshRollouts,
    showAlert,
  ]);

  const globalFilterFn = React.useCallback((row, _columnId, filterValue) => {
    return matchesRolloutSearch(row.original, filterValue);
  }, []);

  const columns = React.useMemo(
    () =>
      [
        canDeleteRollouts || canCreateRollout
          ? {
              id: 'select',
              size: 44,
              minSize: 44,
              maxSize: 44,
              enableResizing: false,
              header: ({ table }) => {
                // Built-in page helpers also count grouped child rows (which
                // select-all never selects), so derive state from visible rows.
                const pageRows = table.getRowModel().rows;
                const allSelected =
                  pageRows.length > 0 && pageRows.every((row) => row.getIsSelected());
                const someSelected =
                  !allSelected && pageRows.some((row) => row.getIsSelected());

                return (
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    aria-label="Select all rows on page"
                    onChange={(event) =>
                      table.toggleAllPageRowsSelected(event.target.checked)
                    }
                  />
                );
              },
              cell: ({ row }) => (
                <Checkbox
                  checked={row.getIsSelected()}
                  indeterminate={row.getIsSomeSelected()}
                  aria-label={`Select ${row.original.rolloutName}`}
                  onChange={row.getToggleSelectedHandler()}
                />
              ),
              enableSorting: false,
              enableGlobalFilter: false,
            }
          : null,
        {
          accessorKey: 'rolloutName',
          id: 'rolloutName',
          size: 420,
          minSize: 320,
          header: ({ column }) => (
            <SortableHeader column={column} label="Rollout" />
          ),
          cell: ({ row }) => {
            const hasTags = normalizeTagList(row.original.tags).length > 0;
            const environment = String(row.original.environment ?? '').trim();
            const rolloutType = String(
              row.original.rolloutTypeLabel ?? ''
            ).trim();
            const isChildRow = row.depth > 0;
            const canExpand = row.getCanExpand();
            const isExpanded = row.getIsExpanded();
            const connectorOffset = row.depth * GROUP_ROW_INDENT_PX;
            const siblingRows = row.parent?.subRows ?? [];
            const isLastSibling =
              siblingRows[siblingRows.length - 1]?.id === row.id;

            return (
              <div
                className="flex min-w-0 items-start gap-3"
                style={
                  isChildRow
                    ? { paddingLeft: `${connectorOffset}px` }
                    : undefined
                }
              >
                {isChildRow ? (
                  <div className="relative mt-1 flex h-8 w-5 shrink-0 items-center justify-center">
                    <span className="absolute left-1/2 top-[-20px] h-[20px] w-px -translate-x-1/2 bg-border/40" />
                    <span className="absolute left-1/2 top-1/2 h-px w-4 -translate-y-1/2 bg-border/40" />
                    {!isLastSibling ? (
                      <span className="absolute left-1/2 bottom-[-20px] top-1/2 w-px -translate-x-1/2 bg-border/40" />
                    ) : null}
                  </div>
                ) : null}

                {!isChildRow ? (
                  <div className="flex h-4 w-3 shrink-0 self-center items-center justify-center">
                    {canExpand ? (
                      <span
                        aria-hidden="true"
                        className={`h-0 w-0 border-y-[4px] border-y-transparent border-l-[6px] border-l-muted-foreground/80 transition-transform ${
                          isExpanded ? 'rotate-90 border-l-primary' : ''
                        }`}
                      />
                    ) : null}
                  </div>
                ) : null}

                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-primary ${
                    isChildRow
                      ? 'mt-0.5 self-start bg-primary/5 text-primary/80'
                      : 'self-center bg-primary/10'
                  }`}
                >
                  <Archive className="h-4 w-4" />
                </div>

                <div className="min-w-0 flex-1 space-y-2">
                  <div
                    className={`whitespace-normal text-[14px] leading-5 text-foreground [overflow-wrap:anywhere] ${
                      isChildRow ? 'font-medium' : 'font-semibold'
                    }`}
                    title={row.original.rolloutName}
                  >
                    {row.original.rolloutName}
                  </div>

                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    {environment ? (
                      <span className={ENVIRONMENT_BADGE_CLASS}>
                        <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                        {environment}
                      </span>
                    ) : null}

                    {rolloutType ? (
                      <span className={ROLLOUT_TYPE_BADGE_CLASS}>
                        <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                        {rolloutType}
                      </span>
                    ) : null}

                    {hasTags ? (
                      <TagBadgeList
                        tags={row.original.tags}
                        maxVisible={3}
                        onOverflowClick={
                          canUpdateTags
                            ? (event) => {
                                event.stopPropagation();
                                handleOpenTagEditor(row.original);
                              }
                            : undefined
                        }
                      />
                    ) : null}

                    {canUpdateTags ? (
                      <button
                        type="button"
                        className={
                          hasTags
                            ? 'inline-flex items-center gap-1.5 rounded-lg border border-border/50 bg-background/80 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-primary/30 hover:text-foreground'
                            : 'inline-flex items-center gap-1.5 rounded-lg border border-primary/45 bg-primary/5 px-2.5 py-1 text-[11px] font-medium text-foreground transition-colors hover:border-primary/70 hover:bg-primary/10'
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenTagEditor(row.original);
                        }}
                      >
                        <PencilLine className="h-3 w-3" />
                        {hasTags ? 'Edit tags' : 'Add tags'}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          },
        },
        {
          accessorKey: 'description',
          id: 'description',
          size: 260,
          minSize: 180,
          header: 'Description',
          enableSorting: false,
          cell: ({ row }) => (
            <RolloutDescriptionCell
              description={
                row.original.description || 'No description provided.'
              }
            />
          ),
        },
        {
          accessorKey: 'createdAt',
          id: 'createdAt',
          size: 150,
          minSize: 132,
          header: ({ column }) => (
            <SortableHeader column={column} label="Created" />
          ),
          sortingFn: (rowA, rowB, columnId) => {
            const first = new Date(rowA.getValue(columnId) || 0).getTime();
            const second = new Date(rowB.getValue(columnId) || 0).getTime();
            return first - second;
          },
          cell: ({ row }) => (
            <div className="text-[13px] font-medium text-foreground">
              {row.original.createdAtLabel}
            </div>
          ),
        },
        {
          accessorKey: 'totalBugs',
          id: 'bugs',
          size: 104,
          minSize: 92,
          header: 'Bugs',
          enableSorting: false,
          cell: ({ row }) =>
            canViewIssues ? (
              <Button
                type="button"
                variant="outline"
                className="h-9 rounded-xl border-border/45 bg-background/30 px-3 text-foreground shadow-none transition-[border-color,background-color,color] duration-150 hover:border-primary/35 hover:bg-primary/8 hover:text-foreground"
                title={`View ${row.original.totalBugs} bugs`}
                onClick={(event) => {
                  event.stopPropagation();
                  setBugsRolloutName(row.original.rolloutName);
                }}
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Bug className="h-3 w-3" />
                </span>
                <span className="ml-2 text-[12px] font-semibold tabular-nums text-foreground">
                  {row.original.totalBugs}
                </span>
              </Button>
            ) : (
              <div className="text-[13px] font-medium text-foreground">
                {row.original.totalBugs}
              </div>
            ),
        },
        canDownloadRollouts
          ? {
              id: 'download',
              size: 176,
              minSize: 168,
              header: 'Download',
              enableSorting: false,
              enableGlobalFilter: false,
              cell: ({ row }) => (
                <div
                  className="flex w-full max-w-[176px] flex-col gap-2"
                  onClick={(event) => event.stopPropagation()}
                >
                  <SplitButton
                    primaryAction={
                      <>
                        {preferredDownloadOption.platformType === 'l' ? (
                          <Archive className="mr-1.5 h-3 w-3" />
                        ) : (
                          <Package2 className="mr-1.5 h-3 w-3" />
                        )}
                        <span className="truncate">
                          {preferredDownloadOption.shortLabel}
                        </span>
                      </>
                    }
                    menuOptions={alternateDownloadOptions}
                    onPrimaryClick={() =>
                      handleDownloadArchive(
                        preferredDownloadOption.platformType,
                        row.original.rolloutName
                      )
                    }
                    onSelect={(option) =>
                      handleDownloadArchive(
                        option.platformType,
                        row.original.rolloutName
                      )
                    }
                    className="w-full"
                    primaryClassName="h-8 px-2.5 text-[11px]"
                    triggerClassName="h-8 w-8"
                    menuClassName="min-w-[170px]"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 rounded-xl border-border/45 bg-background/30 px-2.5 text-[11px] font-medium text-foreground shadow-none transition-[border-color,background-color,color] duration-150 hover:border-primary/35 hover:bg-primary/8 hover:text-foreground"
                    onClick={() => handleOpenVersionDownloadDialog(row.original)}
                  >
                    <Archive className="mr-1.5 h-3 w-3" />
                    <span className="truncate">Versions</span>
                  </Button>
                </div>
              ),
            }
          : null,
      ].filter(Boolean),
    [
      alternateDownloadOptions,
      canCreateRollout,
      canDeleteRollouts,
      canDownloadRollouts,
      canUpdateTags,
      canViewIssues,
      handleOpenTagEditor,
      handleDownloadArchive,
      handleOpenVersionDownloadDialog,
      preferredDownloadOption.platformType,
      preferredDownloadOption.shortLabel,
    ]
  );

  const table = useReactTable({
    data: hydratedRollouts,
    columns,
    state: {
      sorting,
      globalFilter: searchTerm,
      expanded: expandedState,
      rowSelection,
      columnVisibility,
      columnSizing,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearchTerm,
    onExpandedChange: handleExpandedChange,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onPaginationChange: setPagination,
    getSubRows: (row) => row.children ?? [],
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: true,
    enableSubRowSelection: false,
    columnResizeMode: 'onChange',
    globalFilterFn,
    getRowId: getRolloutRowId,
    autoResetExpanded: false,
    filterFromLeafRows: true,
    paginateExpandedRows: false,
  });

  React.useEffect(() => {
    clearSelection();
  }, [searchTerm, selectedTags, clearSelection]);

  React.useEffect(() => {
    setPagination((current) => ({
      ...current,
      pageIndex: 0,
    }));
  }, [searchTerm, selectedTags, sorting]);

  const pageCount = table.getPageCount();
  const selectedRollouts = table
    .getSelectedRowModel()
    .rows.map((row) => row.original);

  React.useEffect(() => {
    if (pagination.pageIndex >= pageCount && pageCount > 0) {
      setPagination((current) => ({
        ...current,
        pageIndex: pageCount - 1,
      }));
    }
  }, [pageCount, pagination.pageIndex]);

  const selectedCount = selectedRollouts.length;
  const filteredCount = table.getFilteredRowModel().rows.length;
  const currentPage = pagination.pageIndex + 1;
  const totalPages = Math.max(1, pageCount);
  const rowsPerPage = String(pagination.pageSize);
  const hasActiveFilters = hasInteractiveFilters;
  const showingFrom = filteredCount
    ? pagination.pageIndex * pagination.pageSize + 1
    : 0;
  const showingTo = filteredCount
    ? Math.min((pagination.pageIndex + 1) * pagination.pageSize, filteredCount)
    : 0;

  const handleSearchChange = React.useCallback(
    (value) => {
      clearAlert();
      setSearchTerm(value);
    },
    [clearAlert]
  );

  const handleTagFilterChange = React.useCallback(
    (nextTags) => {
      clearAlert();
      setSelectedTags(normalizeTagList(nextTags));
    },
    [clearAlert]
  );

  const handleRowsPerPageChange = React.useCallback(
    (value) => {
      clearSelection();
      setPagination({
        pageIndex: 0,
        pageSize: Number(value),
      });
    },
    [clearSelection]
  );

  const handlePageChange = React.useCallback((page) => {
    setPagination((current) => ({
      ...current,
      pageIndex: Math.max(0, page - 1),
    }));
  }, []);

  const handleDeleteSelected = React.useCallback(() => {
    if (!canDeleteRollouts) {
      return;
    }

    setDeleteRolloutNames(
      selectedRollouts.map((rollout) => rollout.rolloutName)
    );
  }, [canDeleteRollouts, selectedRollouts]);

  const handleRecreateSelected = React.useCallback(() => {
    if (!canCreateRollout) {
      return;
    }

    if (selectedRollouts.length !== 1) {
      return;
    }

    const [rollout] = selectedRollouts;
    const recreateRolloutState = {
      ...rolloutContextState,
      recreateRollout: {
        description: String(rollout?.description ?? '').trim(),
        environment: String(rollout?.environment ?? '').trim(),
        tags: normalizeTagList(rollout?.tags),
      },
    };

    navigate(
      `/createrollout/${projectId}/${getRolloutVersionId(
        rollout.version
      )}/recreate/${getRolloutMajorVersion(rollout.rolloutName)}/${rollout.rolloutName}`,
      {
        state: recreateRolloutState,
      }
    );
  }, [
    canCreateRollout,
    navigate,
    projectId,
    rolloutContextState,
    selectedRollouts,
  ]);

  const handleConfirmDelete = React.useCallback(async () => {
    if (!canDeleteRollouts) {
      return;
    }

    if (deleteRolloutNames.length === 0) {
      return;
    }

    setActionLoading(true);
    clearAlert();

    const failedDeletes = [];

    try {
      for (const rolloutName of deleteRolloutNames) {
        const response = await deleteRolloutData(rolloutName);

        if (!response?.isSuccess) {
          failedDeletes.push(rolloutName);
        }
      }

      if (failedDeletes.length > 0) {
        showAlert(
          failedDeletes.length === 1
            ? `Unable to delete rollout ${failedDeletes[0]}.`
            : `Unable to delete ${failedDeletes.length} selected rollouts.`
        );
      } else {
        showAlert(
          deleteRolloutNames.length === 1
            ? `Rollout ${deleteRolloutNames[0]} was deleted successfully.`
            : `${deleteRolloutNames.length} selected rollouts were deleted successfully.`,
          'success'
        );
      }

      setDeleteRolloutNames([]);
      clearSelection();
      await refreshRollouts();
    } catch (error) {
      showAlert(error?.message || 'Unable to delete the selected rollouts.');
    } finally {
      setActionLoading(false);
    }
  }, [
    canDeleteRollouts,
    clearAlert,
    clearSelection,
    deleteRolloutNames,
    refreshRollouts,
    showAlert,
  ]);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        clearAlert();
      }}
    >
      {actionLoading ? <Loader /> : null}

      <div className="tw rollout-list-page">
        <AppPageSection>
          {alertMessage.message ? (
            <div
              className={
                alertMessage.type === 'success'
                  ? 'rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-[13px] text-emerald-700 dark:text-emerald-200'
                  : 'rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-[13px] text-destructive'
              }
            >
              {alertMessage.message}
            </div>
          ) : null}

          <RolloutCatalogHeader
            loading={loading || refreshing}
            projectName={projectName}
            selectedCount={selectedCount}
            versionName={versionName}
            onDeleteSelected={handleDeleteSelected}
            onRecreateSelected={handleRecreateSelected}
            onClearSelection={clearSelection}
            onRefresh={handleRefresh}
            onCreateRollout={handleCreateRollout}
            onCreateAccumulative={handleCreateAccumulative}
            canDeleteSelected={canDeleteRollouts}
            canRecreateSelected={canCreateRollout}
            canCreateRollout={canCreateRollout}
          />

          <RolloutCatalogFilters
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            selectedTags={selectedTags}
            tagOptions={dynamicTagOptions}
            onTagSearchChange={setFilterTagSearchQuery}
            onTagsChange={handleTagFilterChange}
            tagsLoading={filterTagSuggestionsLoading}
            showingFrom={showingFrom}
            showingTo={showingTo}
            filteredCount={filteredCount}
            reportedTotal={reportedTotal}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
          />

          {loading && rollouts.length === 0 ? (
            <RolloutCatalogSkeleton
              count={Math.min(Number(rowsPerPage || 10), 6)}
            />
          ) : errorMessage ? (
            <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-[13px] text-destructive">
              {errorMessage}
            </div>
          ) : filteredCount === 0 ? (
            <EmptyState
              icon={Archive}
              title={
                hasActiveFilters
                  ? 'No rollouts matched your search'
                  : 'No rollouts available'
              }
              description={
                hasActiveFilters
                  ? 'Try a different search or remove one of the selected tag filters.'
                  : 'Generated rollouts will appear here once they are available.'
              }
            />
          ) : (
            <>
              <RolloutCatalogTable table={table} refreshing={refreshing} />

              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                summary={`Showing ${showingFrom}-${showingTo} of ${filteredCount} results`}
              />
            </>
          )}
        </AppPageSection>
      </div>

      <Dialog
        open={downloadDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseDownloadDialog();
          }
        }}
      >
        <DialogContent className="gap-0 overflow-hidden rounded-2xl border border-border/40 bg-card/95 p-0 shadow-2xl sm:max-w-[760px]">
          <div className="relative flex min-h-[360px] max-h-[min(82vh,640px)] flex-col">
            <div className="shrink-0 border-b border-border/35 px-5 py-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 space-y-1.5">
                  <DialogTitle>Download rollout</DialogTitle>
                  <p className="text-[13px] text-muted-foreground">
                    The rollout has been created successfully. Select a configured version, review its details, then continue with the download.
                  </p>
                  <p className="truncate font-mono text-[12px] text-foreground/80">
                    {downloadDialogRollout.rolloutName || 'No rollout selected'}
                  </p>
                </div>

                <div className="inline-flex shrink-0 items-center rounded-full border border-border/45 bg-background/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  {configuredVersionOptions.length} versions
                </div>
              </div>
            </div>

            <div className="themed-slim-scrollbar flex-1 overflow-y-auto px-5 py-5">
              <div className="space-y-5">
                {configuredVersionOptions.length ? (
                  <>
                    <div className="space-y-2">
                      <p className="text-[12px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                        Available Versions
                      </p>
                      <Select
                        value={selectedConfiguredVersion}
                        onValueChange={(value) => {
                          clearAlert();
                          setSelectedConfiguredVersion(value);
                        }}
                      >
                        <SelectTrigger className="h-11 rounded-xl border-border/45 bg-background/40 text-left text-[14px] text-foreground shadow-none">
                          <SelectValue placeholder="Select a configured version" />
                        </SelectTrigger>
                        <SelectContent>
                          {configuredVersionOptions.map((versionOption) => (
                            <SelectItem key={versionOption.value} value={versionOption.value}>
                              {versionOption.isDefault
                                ? `${versionOption.label} (Default)`
                                : versionOption.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedConfiguredVersionOption ? (
                      <div className="rounded-2xl border border-border/45 bg-background/45 p-5">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-[18px] font-semibold text-foreground">
                            {selectedConfiguredVersionOption.label}
                          </h3>
                          {selectedConfiguredVersionOption.isDefault ? (
                            <span className="rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-700 dark:border-amber-400/25 dark:bg-amber-400/10 dark:text-amber-200">
                              Default
                            </span>
                          ) : null}
                        </div>

                        <div className="mt-4 grid gap-3 sm:grid-cols-3">
                          <div className="rounded-xl border border-border/40 bg-background/55 p-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                              Version
                            </p>
                            <p className="mt-1 text-[15px] font-semibold text-foreground">
                              {selectedConfiguredVersionOption.label}
                            </p>
                          </div>
                          <div className="rounded-xl border border-border/40 bg-background/55 p-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                              mcs-component
                            </p>
                            <p className="mt-1 font-mono text-[14px] text-foreground">
                              {String(selectedConfiguredVersionOption.majorVersion ?? '').trim() ||
                                'Not configured'}
                            </p>
                          </div>
                          <div className="rounded-xl border border-border/40 bg-background/55 p-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                              rpcommon
                            </p>
                            <p className="mt-1 font-mono text-[14px] text-foreground">
                              {String(selectedConfiguredVersionOption.minorVersion ?? '').trim() ||
                                'Not configured'}
                            </p>
                          </div>
                        </div>

                        <p className="mt-4 text-[12px] text-muted-foreground">
                          Combined value: {formatConfiguredVersionValue(selectedConfiguredVersionOption)}
                        </p>

                        {!String(selectedConfiguredVersionOption?.value ?? '').trim() ? (
                          <p className="mt-3 text-[12px] leading-5 text-amber-600 dark:text-amber-300">
                            This configured version is missing its environment value. Update it in Settings to enable download.
                          </p>
                        ) : null}
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-dashed border-border/50 bg-background/30 px-4 py-5 text-[13px] text-muted-foreground">
                        Select a configured version to view its major and minor values.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="rounded-2xl border border-dashed border-border/50 bg-background/30 px-4 py-5 text-[13px] text-muted-foreground">
                    No configured versions were found. Download will use the backend default configured version if one is available.
                  </div>
                )}

                {canDownloadRollouts ? (
                  <div className="flex justify-center pt-1">
                    <Button
                      type="button"
                      className="h-10 min-w-[144px] rounded-xl bg-primary px-4 text-[12px] font-medium text-primary-foreground hover:bg-primary/90"
                      disabled={
                        !downloadDialogRollout.platformType ||
                        !downloadDialogRollout.rolloutName ||
                        (configuredVersionOptions.length > 0 &&
                          !String(selectedConfiguredVersionOption?.value ?? '').trim()) ||
                        actionLoading
                      }
                      onClick={handleDownloadCreatedRollout}
                    >
                      {downloadDialogPlatformOption?.platformType === 'l' ? (
                        <Archive className="mr-1.5 h-3.5 w-3.5" />
                      ) : (
                        <Package2 className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      {downloadDialogPlatformOption?.shortLabel
                        ? `Download ${downloadDialogPlatformOption.shortLabel}`
                        : 'Download'}
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="shrink-0 border-t border-border/35 bg-background/70 px-5 py-4">
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseDownloadDialog}
                  disabled={actionLoading}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={versionDownloadDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseVersionDownloadDialog();
          }
        }}
      >
        <DialogContent className="gap-0 overflow-hidden rounded-2xl border border-border/40 bg-card/95 p-0 shadow-2xl sm:max-w-[760px]">
          <div className="relative flex min-h-[360px] max-h-[min(82vh,640px)] flex-col">
            <div className="shrink-0 border-b border-border/35 px-5 py-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 space-y-1.5">
                  <DialogTitle>Download rollout for a configured version</DialogTitle>
                  <p className="text-[13px] text-muted-foreground">
                    Select a configured version, review its details, then download either ZIP or TAR.
                  </p>
                  <p className="truncate font-mono text-[12px] text-foreground/80">
                    {versionDownloadDialog.rolloutName || 'No rollout selected'}
                  </p>
                </div>

                <div className="inline-flex shrink-0 items-center rounded-full border border-border/45 bg-background/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  {configuredVersionOptions.length} versions
                </div>
              </div>
            </div>

            <div className="themed-slim-scrollbar flex-1 overflow-y-auto px-5 py-5">
              {configuredVersionOptions.length ? (
                <div className="space-y-5">
                  <div className="space-y-2">
                    <p className="text-[12px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                      Available Versions
                    </p>
                    <Select
                      value={selectedConfiguredVersion}
                      onValueChange={(value) => {
                        clearAlert();
                        setSelectedConfiguredVersion(value);
                      }}
                    >
                      <SelectTrigger className="h-11 rounded-xl border-border/45 bg-background/40 text-left text-[14px] text-foreground shadow-none">
                        <SelectValue placeholder="Select a configured version" />
                      </SelectTrigger>
                      <SelectContent>
                        {configuredVersionOptions.map((versionOption) => (
                          <SelectItem key={versionOption.value} value={versionOption.value}>
                            {versionOption.isDefault
                              ? `${versionOption.label} (Default)`
                              : versionOption.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedConfiguredVersionOption ? (
                    <div className="rounded-2xl border border-border/45 bg-background/45 p-5">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-[18px] font-semibold text-foreground">
                          {selectedConfiguredVersionOption.label}
                        </h3>
                        {selectedConfiguredVersionOption.isDefault ? (
                          <span className="rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-700 dark:border-amber-400/25 dark:bg-amber-400/10 dark:text-amber-200">
                            Default
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-4 grid gap-3 sm:grid-cols-3">
                        <div className="rounded-xl border border-border/40 bg-background/55 p-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            Version
                          </p>
                          <p className="mt-1 text-[15px] font-semibold text-foreground">
                            {selectedConfiguredVersionOption.label}
                          </p>
                        </div>
                        <div className="rounded-xl border border-border/40 bg-background/55 p-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            mcs-component
                          </p>
                          <p className="mt-1 font-mono text-[14px] text-foreground">
                            {String(selectedConfiguredVersionOption.majorVersion ?? '').trim() ||
                              'Not configured'}
                          </p>
                        </div>
                        <div className="rounded-xl border border-border/40 bg-background/55 p-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            rpcommon
                          </p>
                          <p className="mt-1 font-mono text-[14px] text-foreground">
                            {String(selectedConfiguredVersionOption.minorVersion ?? '').trim() ||
                              'Not configured'}
                          </p>
                        </div>
                      </div>

                      <p className="mt-4 text-[12px] text-muted-foreground">
                        Combined value: {formatConfiguredVersionValue(selectedConfiguredVersionOption)}
                      </p>

                      {!String(selectedConfiguredVersionOption?.value ?? '').trim() ? (
                        <p className="mt-3 text-[12px] leading-5 text-amber-600 dark:text-amber-300">
                          This configured version is missing its environment value. Update it in Settings to enable download.
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-border/50 bg-background/30 px-4 py-5 text-[13px] text-muted-foreground">
                      Select a configured version to view its major and minor values.
                    </div>
                  )}

                  <div className="flex justify-center pt-1">
                    <div className="flex flex-wrap justify-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        className="h-10 min-w-[112px] rounded-xl border-border/45 bg-background/30 px-4 text-[12px] font-medium text-foreground shadow-none transition-[border-color,background-color,color] duration-150 hover:border-primary/35 hover:bg-primary/8 hover:text-foreground"
                        disabled={
                          !String(selectedConfiguredVersionOption?.value ?? '').trim() ||
                          actionLoading
                        }
                        onClick={() => handleDownloadConfiguredVersion('w')}
                      >
                        <Package2 className="mr-1.5 h-3.5 w-3.5" />
                        ZIP
                      </Button>
                      <Button
                        type="button"
                        className="h-10 min-w-[112px] rounded-xl bg-primary px-4 text-[12px] font-medium text-primary-foreground hover:bg-primary/90"
                        disabled={
                          !String(selectedConfiguredVersionOption?.value ?? '').trim() ||
                          actionLoading
                        }
                        onClick={() => handleDownloadConfiguredVersion('l')}
                      >
                        <Archive className="mr-1.5 h-3.5 w-3.5" />
                        TAR
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-border/50 bg-background/30 px-4 py-5 text-[13px] text-muted-foreground">
                  No configured versions were found. Add them in Settings &gt; Rollout Package first.
                </div>
              )}
            </div>

            <div className="shrink-0 border-t border-border/35 bg-background/70 px-5 py-4">
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseVersionDownloadDialog}
                  disabled={actionLoading}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(editingRollout)}
        onOpenChange={(open) => {
          if (!open && !savingTags) {
            handleCloseTagEditor();
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit rollout tags</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-[13px] font-medium text-foreground">
                {editingRollout?.rolloutName}
              </p>
              <p className="text-[12px] text-muted-foreground">
                Add existing tags, create new ones, or remove tags that no
                longer apply.
              </p>
            </div>

            <TagInput
              value={editingTags}
              options={editorTagOptions}
              onChange={setEditingTags}
              onSearchChange={setEditorTagSearchQuery}
              loading={editorTagSuggestionsLoading}
              placeholder="Search or create tags"
              searchPlaceholder="Find tags or create a new one"
              emptyMessage="No matching tags"
              createMessage="Create tag"
            />

            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseTagEditor}
                disabled={savingTags}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={handleSaveTags}
                disabled={savingTags}
              >
                {savingTags ? 'Saving...' : 'Save tags'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteRolloutNames.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteRolloutNames([]);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {deleteRolloutNames.length > 1
                ? 'Delete rollouts'
                : 'Delete rollout'}
            </DialogTitle>
          </DialogHeader>

          <p className="text-[13px] leading-6 text-muted-foreground">
            {deleteRolloutNames.length > 1 ? (
              `This will permanently delete ${deleteRolloutNames.length} selected rollouts. This action cannot be undone.`
            ) : (
              <>
                This will permanently delete{' '}
                <span className="font-semibold text-foreground">
                  {deleteRolloutNames[0]}
                </span>
                . This action cannot be undone.
              </>
            )}
          </p>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteRolloutNames([])}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmDelete}
            >
              {deleteRolloutNames.length > 1
                ? `Delete ${deleteRolloutNames.length} rollouts`
                : 'Delete rollout'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <RolloutBugsDialog
        open={Boolean(bugsRolloutName)}
        onOpenChange={(open) => {
          if (!open) {
            setBugsRolloutName(null);
          }
        }}
        rolloutName={bugsRolloutName}
        onDownloadAllBugs={handleDownloadAllBugs}
        onDownloadBug={handleDownloadBug}
        preferredPlatformType={preferredDownloadOption.platformType}
        canDownloadRollouts={canDownloadRollouts}
      />
    </ErrorBoundary>
  );
};

export default RolloutList;
